import React, { useState, useEffect, useCallback } from 'react'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

export default function AdminBuyPanel({ token, onStatsChange }) {
  const [inquiries, setInquiries] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const fetchInquiries = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/admin/buy-inquiries', { headers: { 'x-admin-token': token } })
      const data = await res.json()
      setInquiries(Array.isArray(data) ? data.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)) : [])
    } catch {
      setInquiries([])
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchInquiries() }, [fetchInquiries])

  const archive = async (id) => {
    await fetch(`/api/admin/buy-inquiries/${id}/read`, { method: 'PATCH', headers: { 'x-admin-token': token } })
    fetchInquiries()
    if (onStatsChange) onStatsChange()
  }

  const deleteRequest = async () => {
    if (!deleteConfirm) return
    setLoading(true)
    try {
      await fetch(`/api/admin/buy-inquiries/${deleteConfirm}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
    } finally {
      setDeleteConfirm(null)
      fetchInquiries()
      if (onStatsChange) onStatsChange()
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}30`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  )

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Buy Inquiries</h3>
          <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: 0 }}>{inquiries.length} request{inquiries.length !== 1 ? 's' : ''} total</p>
        </div>
        <button onClick={fetchInquiries} style={{ padding: '9px 16px', borderRadius: '8px', border: `1.5px solid ${GOLD}40`, background: `${GOLD}08`, color: NAVY, fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>↻ Refresh</button>
      </div>

      {inquiries.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '80px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,46,83,0.06)' }}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}>💎</div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 8px' }}>No buy inquiries yet</h3>
          <p style={{ fontFamily: FONT, fontSize: '14px', color: '#94a3b8', margin: 0 }}>Submissions from the Buy Your Dream Property page will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '12px' }}>
          {inquiries.map(s => {
            const f = s.form || {}
            return (
              <div key={s.id} style={{ background: '#fff', borderRadius: '14px', padding: '20px', borderLeft: `4px solid ${s.status === 'archived' ? '#94a3b8' : GOLD}`, boxShadow: '0 2px 8px rgba(0,46,83,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <h4 style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>{f.firstName} {f.lastName}</h4>
                    <span style={{ fontFamily: FONT, fontSize: '12px', color: '#64748b' }}>Submitted {new Date(s.submittedAt).toLocaleDateString()} {new Date(s.submittedAt).toLocaleTimeString()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {s.status !== 'archived' && (
                      <button onClick={() => archive(s.id)} style={{ padding: '6px 12px', borderRadius: '6px', background: `${NAVY}10`, border: 'none', color: NAVY, fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}>Mark as Read</button>
                    )}
                    <button onClick={() => setDeleteConfirm(s.id)} style={{ padding: '6px 12px', borderRadius: '6px', background: 'transparent', border: '1px solid #fca5a5', color: '#dc2626', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}>Delete</button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', background: '#f8fafb', padding: '12px', borderRadius: '8px' }}>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Contact</div>
                    <div style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, fontWeight: 500 }}>{f.email} <br/> {f.phone}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Property Type</div>
                    <div style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, fontWeight: 500, textTransform: 'capitalize' }}>{f.propertyType || 'Any'}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Budget</div>
                    <div style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, fontWeight: 500 }}>{f.budget || 'Not specified'}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Location</div>
                    <div style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, fontWeight: 500 }}>{f.location || 'Any'}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Bedrooms</div>
                    <div style={{ fontFamily: FONT, fontSize: '14px', color: NAVY, fontWeight: 500 }}>{f.bedrooms || 'Any'}</div>
                  </div>
                  {f.message && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Requirements</div>
                      <div style={{ fontFamily: FONT, fontSize: '14px', color: '#4a5568' }}>{f.message}</div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Custom Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,16,32,0.6)', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: `1px solid #fca5a5`, animation: 'fadeIn 0.2s ease' }}>
            <div style={{ fontSize: '36px', marginBottom: '16px', textAlign: 'center' }}>🗑️</div>
            <h3 style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, textAlign: 'center', margin: '0 0 12px' }}>Delete Inquiry?</h3>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#64748b', textAlign: 'center', margin: '0 0 24px', lineHeight: 1.6 }}>Are you sure you want to permanently delete this sales inquiry? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #d1d5db', background: '#fff', color: NAVY, fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafb'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>Cancel</button>
              <button onClick={deleteRequest} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#fff', fontFamily: FONT, fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(220,38,38,0.2)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'} onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
